<template>
  <div class="memberScrollRegion">
    <!-- ── Header row: same two-part structure as a data row ── -->
    <div class="memberRowOuter memberHeaderOuter">
      <div class="frozenSubRow frozenHeaderSubRow">
        <div class="headerCheckbox" />
        <div class="frozenSlotBox" :style="frozenColumnStyle"><slot name="frozen-header" /></div>
      </div>
      <div class="scrollSubRow memberHeader" :style="gridStyle">
        <slot name="header" />
      </div>
    </div>

    <div v-if="rows.length > 0" class="memberList">
      <!-- ── One row wrapper per item, holding its frozen and scrolling
           sub-rows as flex siblings. `align-items: stretch` (the flex
           default) is what forces them to the same height -- they can
           never drift apart the way two independently-rendered row lists
           could, because there is exactly one row per data item. ── -->
      <div
        v-for="row in rows"
        :key="rowKey(row)"
        class="memberRowOuter"
        :class="{ memberRowSelected: isSelected(row) }"
        :data-membership-id="rowKey(row)"
        @click="$emit('open-row', row)"
      >
        <div class="frozenSubRow">
          <div class="checkboxCol" @click.stop>
            <input
              type="checkbox"
              :checked="isChecked(row)"
              @change="$emit('toggle-select', row, $event)"
            />
          </div>
          <div class="frozenSlotBox" :style="frozenColumnStyle"><slot name="frozen-row" :row="row" /></div>
        </div>
        <div class="scrollSubRow memberRow" :style="gridStyle">
          <slot name="row" :row="row" />
        </div>
      </div>
    </div>

    <div v-else-if="!loading" class="empty">
      <p class="emptyText"><slot name="empty">No candidates.</slot></p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

/**
 * Shared grid/sticky-column scaffold for the Account, Contact, and Engaged
 * candidate tables. Each row is ONE flex wrapper (`.memberRowOuter`)
 * containing two sub-rows as siblings: a frozen sub-row (checkbox + one
 * identity column, `position: sticky`) and a scrolling sub-row (every other
 * column). Flex's default `align-items: stretch` forces both to the same
 * height automatically -- there's no risk of the frozen and scrolling sides
 * drifting apart the way two independently-rendered row lists could, because
 * there's exactly one row per data item, not two.
 */

const props = defineProps<{
  rows: any[]
  rowKey: (row: any) => string
  /** Drives the row highlight -- true for a checkbox-selected row OR the row whose drawer is open. */
  isSelected: (row: any) => boolean
  /** Drives the checkbox's own checked state -- bulk-selection only, independent of the drawer. */
  isChecked: (row: any) => boolean
  loading?: boolean
  /** e.g. "260px" — the frozen sub-row's one identity column (checkbox is separate, fixed at 32px). */
  frozenColumnWidth: string
  /** e.g. "220px 140px 90px 110px 80px 220px" — every column after the frozen one. */
  gridTemplateColumns: string
}>()

defineEmits<{
  'open-row': [any]
  'toggle-select': [any, Event]
}>()

const gridStyle = computed(() => ({
  gridTemplateColumns: props.gridTemplateColumns,
}))

const frozenColumnStyle = computed(() => ({
  width: props.frozenColumnWidth,
  minWidth: props.frozenColumnWidth,
}))
</script>

<style scoped>
/* ── Scroll region: frozen sub-row pinned per-row, scrolling sub-row
   scrolls with the region. Fixed column widths (not minmax(0, Nfr)) so
   columns keep a real, readable width instead of being crushed when the
   drawer narrows the viewport -- there's a horizontal scrollbar for that
   instead.
   Square top and no top border: the toolbar directly above supplies the
   divider, so the bar and the table read as one panel -- same treatment
   as the Actions page's .listScroll. ── */
.memberScrollRegion {
  overflow-x: auto;
  scrollbar-width: none; /* Firefox */
  border: 1px solid var(--color-border-subtle);
  border-top: none;
  border-radius: 0 0 var(--radius-lg) var(--radius-lg);
  background: var(--color-bg-app);
}
.memberScrollRegion::-webkit-scrollbar { display: none; } /* Chrome, Safari, Edge */

/* width: max-content -- without it this flex column shrinks to the visible
   viewport width instead of the rows' actual (wider, horizontally-scrollable)
   content width. Each row's border-top is drawn on ITS OWN box, so once a
   row shrinks narrower than its content, the overflowing part (revealed
   only by scrolling) sits entirely outside that box and never gets a
   border at all -- which is exactly why the divider line vanished past
   the point where scrolling used to be needed to see it. */
.memberList { display: flex; flex-direction: column; width: max-content; min-width: 100%; }

/* ── Row wrapper: one flex box per item. `align-items: stretch` (default)
   is what forces the frozen and scrolling sub-rows to the same height. ── */
.memberRowOuter {
  display: flex;
  border-top: 1px solid var(--color-border-subtle);
  cursor: pointer;
  transition: background var(--duration-fast) var(--ease-out);
}
.memberRowOuter:first-child { border-top: none; }
.memberHeaderOuter {
  border-top: none;
  border-bottom: 1px solid var(--color-border-subtle);
  cursor: default;
}

/* ── Frozen sub-row: checkbox + one identity column, pinned left.
   Its own padding/background/selection-rail are painted here directly --
   a sticky child doesn't inherit those from an ancestor once that
   ancestor's own box scrolls out of view underneath it, so this sub-row
   has to be fully self-contained rather than borrowing from the row. ── */
.frozenSubRow {
  position: sticky;
  left: 0;
  z-index: 1;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-2);
  background: var(--color-bg-app);
}
.frozenHeaderSubRow {
  padding: var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wide);
  color: var(--color-text-tertiary);
}
.headerCheckbox {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  min-width: 32px;
}

.checkboxCol {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  min-width: 32px;
}

/* ── Scrolling sub-row: every other column ── */
.scrollSubRow {
  flex: 1 1 auto;
  display: grid;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-2);
  min-width: max-content;
}
.memberHeader {
  padding: var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wide);
  color: var(--color-text-tertiary);
}
.memberRow {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-text-primary);
}

/* ── Selection: darkens both sub-rows and adds an accent rail on the
   frozen one. It deliberately does NOT go white: --color-bg-surface is
   #FFFFFF, which on this cream ground reads as an empty row rather than a
   selected one. ── */
.memberRowSelected .scrollSubRow {
  background: var(--color-bg-subtle);
}
.memberRowSelected .frozenSubRow {
  background: var(--color-bg-subtle);
  box-shadow: inset 2px 0 0 var(--color-accent);
}

.empty { padding: var(--space-5) 0; }
.emptyText {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--fs-base);
  color: var(--color-text-secondary);
}
</style>
